/**
 * Pure hierarchy mode helpers — mirrors backend workItemHierarchy.ts and Org Settings.
 * Kept free of React/RTK so tree tests and Backlog logic can import without side effects.
 */
import { WorkItemType } from '@/types';

export type HierarchyMode = 'FULL' | 'LEAN' | 'FEATURE_FIRST';

export const DEFAULT_HIERARCHY_MODE: HierarchyMode = 'FEATURE_FIRST';

export interface HierarchyDescriptor {
  mode: HierarchyMode;
  label: string;
  description: string;
  chain: WorkItemType[];
  allowedTypes: WorkItemType[];
}

const DESCRIPTORS: Record<HierarchyMode, HierarchyDescriptor> = {
  FULL: {
    mode: 'FULL',
    label: 'Full Enterprise Stack',
    description: 'Epic → Feature → Story → Task / Bug. Best for large teams with PMs, designers & engineers working across releases.',
    chain: [WorkItemType.EPIC, WorkItemType.FEATURE, WorkItemType.USER_STORY],
    allowedTypes: [WorkItemType.EPIC, WorkItemType.FEATURE, WorkItemType.USER_STORY, WorkItemType.TASK, WorkItemType.BUG],
  },
  LEAN: {
    mode: 'LEAN',
    label: 'Lean — No Feature Layer',
    description: 'Epic → Story → Task / Bug. Skip Feature grouping. Ideal for startups or small agile squads.',
    chain: [WorkItemType.EPIC, WorkItemType.USER_STORY],
    allowedTypes: [WorkItemType.EPIC, WorkItemType.USER_STORY, WorkItemType.TASK, WorkItemType.BUG],
  },
  FEATURE_FIRST: {
    mode: 'FEATURE_FIRST',
    label: 'Feature-First',
    description: 'Feature → Story → Task / Bug. No epics — Features are the top-level container. Good for product-led teams.',
    chain: [WorkItemType.FEATURE, WorkItemType.USER_STORY],
    allowedTypes: [WorkItemType.FEATURE, WorkItemType.USER_STORY, WorkItemType.TASK, WorkItemType.BUG],
  },
};

export const HIERARCHY_OPTIONS: HierarchyDescriptor[] = [
  DESCRIPTORS.FULL,
  DESCRIPTORS.LEAN,
  DESCRIPTORS.FEATURE_FIRST,
];

export function readHierarchyModeFromSettings(settings: unknown): HierarchyMode {
  const raw = (settings as { hierarchyMode?: string } | null)?.hierarchyMode;
  if (raw == null || raw === '') return DEFAULT_HIERARCHY_MODE;
  // Normalize so "lean" / "feature-first" still resolve correctly.
  const normalized = String(raw).trim().toUpperCase().replace(/-/g, '_');
  if (normalized === 'FULL' || normalized === 'LEAN' || normalized === 'FEATURE_FIRST') {
    return normalized;
  }
  return DEFAULT_HIERARCHY_MODE;
}

export function getHierarchyDescriptor(mode: HierarchyMode): HierarchyDescriptor {
  return DESCRIPTORS[mode] ?? DESCRIPTORS[DEFAULT_HIERARCHY_MODE];
}

export function isAllowedForHierarchyMode(mode: HierarchyMode, type: WorkItemType | string): boolean {
  const allowedSet = new Set<string>(getHierarchyDescriptor(mode).allowedTypes);
  return allowedSet.has(String(type));
}
